import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HubIcon from "@mui/icons-material/Hub";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import TuneIcon from "@mui/icons-material/Tune";
import { COLORS } from "./constants";
import { panelSx, secondaryButtonSx, sectionSubtleSx, sectionTitleSx } from "./styles";
import { platformIcon, platformName, formatUptime } from "./utils";
import { ConfirmDialog } from "./ConfirmDialog";
import { StatusBadge } from "./StatusBadge";

// ─────────────────────────────────────────────────────────────────────────────
// DestinationsPanel
//
// Every OUTPUT in one always-visible list: "where is my stream going right now,
// and is it arriving?" is the question this app exists to answer.
//
// The controls mirror what the API will actually accept, so we never fire a
// request whose only possible outcome is a 404 the user can't interpret:
//
//   POST /api/stream-control/stop/:platform
//     404 "Platform not live" unless state.copies[platform] exists.
//
//   POST /api/stream-control/restart/:platform
//     404 "User not live" unless the user has an activeStreams entry at all.
//     It does NOT require this platform to be running, so on a stopped
//     destination it is a cold START, and the button says so.
//
// A destination cannot be started while nothing is publishing: destinations
// only come into existence through NMS's prePublish. So while the ingest is
// idle a configured destination shows READY with no controls (it will go live
// with the next stream) instead of a row of disabled buttons.
//
// The SRT relay is not listed: it is a source, controlled from the SRT tab.
//
// Stopping a live destination ends the broadcast for that platform's viewers,
// so it asks first.
// ─────────────────────────────────────────────────────────────────────────────

const iconBtnSx = (color) => ({
  width: 38, height: 38, color, border: `1px solid ${COLORS.border}`, borderRadius: "11px",
  "&:hover": { bgcolor: "rgba(255,255,255,0.05)", borderColor: COLORS.borderStrong }
});

// "YouTube · test", but not "TikTok · TikTok" or "YouTube · YouTube 1".
const rowTitle = (platform) => {
  const name = platformName(platform.kind);
  return platform.label.toLowerCase().startsWith(name.toLowerCase())
    ? { name: platform.label, detail: null }
    : { name, detail: platform.label };
};

export function DestinationsPanel({
  platforms, streamStatus, isLive, retryingIds,
  restartPlatformApi, stopPlatformApi,
  fileStreamLive, platformStats = {}, onManage, problems = []
}) {
  // Each destination carries its OWN clock. An FFmpeg copy gets a fresh
  // startedAt whenever that platform respawns after a drop, so "2m 10s" next to
  // others on "1h 4m 30s" says it reconnected.
  const [now, setNow] = useState(() => Date.now());
  // Target outlives `open` so the dialog text doesn't blank out while it fades.
  const [stopTarget, setStopTarget] = useState(null);
  const [stopOpen,   setStopOpen]   = useState(false);

  const hasTimings = Object.values(platformStats).some((s) => s?.startedAt);

  useEffect(() => {
    if (!hasTimings) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [hasTimings]);

  // Problems the server reported for a specific destination (ProblemAlerts
  // shows the full text above; the row only flags which one it is about).
  const problemFor = (platform) => problems.find((p) => p.platform === platform.id) || null;

  const stateOf = (platform) => {
    if (streamStatus[platform.id] === "live") return "live";
    const problem = problemFor(platform);
    if (problem?.severity === "error") return "error";
    // Inferred, not reported: see the retryingIds note in Home/index.jsx.
    if (retryingIds?.has(platform.id) || problem?.severity === "warning") return "retrying";
    return isLive || fileStreamLive ? "offline" : "ready";
  };

  const sending = platforms.filter((p) => stateOf(p) === "live").length;
  const subtitle = platforms.length === 0
    ? "None set up yet"
    : isLive || fileStreamLive
      ? `${sending} of ${platforms.length} sending`
      : `${platforms.length} ready · nothing sending yet`;

  return (
    <Box sx={panelSx}>
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" mb={{ xs: 1.5, sm: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
          <Box sx={{ display: { xs: "none", sm: "flex" },
            width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
            bgcolor: "rgba(43,189,175,0.12)", border: "1px solid rgba(43,189,175,0.35)",
            alignItems: "center", justifyContent: "center"
          }}>
            <HubIcon sx={{ color: COLORS.cyan, fontSize: 19 }} />
          </Box>
          <Box minWidth={0}>
            <Typography sx={sectionTitleSx}>Destinations</Typography>
            <Typography sx={sectionSubtleSx}>{subtitle}</Typography>
          </Box>
        </Stack>
        {onManage && (
          <Button size="small" variant="outlined" startIcon={<TuneIcon />} onClick={onManage}
            sx={{ ...secondaryButtonSx, alignSelf: "center", flexShrink: 0 }}>
            Manage
          </Button>
        )}
      </Stack>

      <Stack spacing={1.25}>
        {platforms.map((platform) => {
          const state   = stateOf(platform);
          const running = state === "live" || state === "retrying";
          const { name, detail } = rowTitle(platform);

          // A destination can be live because a SCHEDULED MP4 job feeds it. Those
          // processes belong to the file scheduler, which /api/stream-control
          // can't touch, so no controls are offered for them.
          const viaFileJob = running && !isLive && fileStreamLive;
          const canStop    = isLive && running;
          const canStart   = isLive && !running;

          const stats = platformStats[platform.id];
          const uptimeSecs = stats?.startedAt ? Math.floor((now - stats.startedAt) / 1000) : null;
          const meta = [];
          if (uptimeSecs != null && uptimeSecs >= 0) meta.push(`${formatUptime(uptimeSecs) || "0s"} on air`);
          // Per destination, not the aggregate: one destination lagging shows
          // up here and nowhere else.
          if (stats?.bitrate) meta.push(`${(stats.bitrate / 1000).toFixed(2)} Mbps`);
          if (state === "retrying") meta.push("reconnecting automatically");
          if (state === "error") meta.push("needs attention · see the alert above");
          if (viaFileJob) meta.push("fed by a scheduled MP4");

          return (
            <Box key={platform.id} sx={{
              display: "flex", alignItems: "center", gap: { xs: 1.25, sm: 1.5 },
              p: { xs: "10px 12px", sm: 1.5 }, borderRadius: { xs: "12px", sm: "14px" },
              border: `1px solid ${state === "live" ? "rgba(61,214,140,0.34)"
                : state === "retrying" ? "rgba(244,183,64,0.36)"
                : state === "error" ? "rgba(229,72,77,0.40)" : COLORS.border}`,
              bgcolor: state === "live" ? "rgba(61,214,140,0.08)"
                : state === "retrying" ? "rgba(244,183,64,0.08)"
                : state === "error" ? COLORS.dangerSoft : COLORS.panelSoft
            }}>
              {platformIcon(platform.kind, 36)}

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ color: COLORS.text, fontWeight: 600, fontSize: 15 }}>
                  {name}
                  {detail && <Box component="span" sx={{ color: COLORS.muted, fontWeight: 400 }}> · {detail}</Box>}
                </Typography>
                {meta.length > 0 && (
                  <Typography sx={{ color: COLORS.muted, fontSize: 12, mt: 0.25, fontVariantNumeric: "tabular-nums",
                    whiteSpace: { xs: "normal", sm: "nowrap" }, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {meta.join(" · ")}
                  </Typography>
                )}
              </Box>

              <StatusBadge state={state} />

              {canStart && (
                <Tooltip title={`Start sending to ${name}`}>
                  <IconButton aria-label={`Start ${name}`} onClick={() => restartPlatformApi(platform.id)}
                    sx={iconBtnSx(COLORS.cyan)}>
                    <PlayArrowIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canStop && (
                <>
                  {/* Not on phones: two buttons left the destination's name about
                      ten characters. Stop stays; reconnecting is rarely needed there. */}
                  <Tooltip title="Restart">
                    <IconButton aria-label={`Restart ${name}`} onClick={() => restartPlatformApi(platform.id)}
                      sx={{ ...iconBtnSx(COLORS.muted), display: { xs: "none", sm: "inline-flex" } }}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={`Stop sending to ${name}`}>
                    <IconButton aria-label={`Stop ${name}`} onClick={() => { setStopTarget({ ...platform, name }); setStopOpen(true); }}
                      sx={iconBtnSx(COLORS.red)}>
                      <StopRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          );
        })}

        {platforms.length === 0 && (
          // This is exactly the state in which NMS rejects a publish with "no
          // destinations", which reaches OBS as a bare disconnect.
          <Box sx={{ p: 2, borderRadius: "14px", border: "1px solid rgba(244,183,64,0.30)", bgcolor: "rgba(244,183,64,0.08)" }}>
            <Typography sx={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>No destinations yet</Typography>
            <Typography sx={{ color: COLORS.textSoft, fontSize: 13, lineHeight: 1.55, mt: 0.5 }}>
              Add a stream key or connect an account. Until then the server turns incoming
              streams away, and OBS just shows a dropped connection.
            </Typography>
          </Box>
        )}

        {onManage && (
          <Box component="button" type="button" onClick={onManage} sx={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
            height: 48, width: "100%", cursor: "pointer", font: "inherit",
            fontSize: 14, fontWeight: 600, color: COLORS.cyan, bgcolor: "transparent",
            border: `1.5px dashed ${COLORS.borderStrong}`, borderRadius: "14px",
            "&:hover": { bgcolor: "rgba(43,189,175,0.08)" }
          }}>
            <AddIcon sx={{ fontSize: 18 }} />
            {platforms.length === 0 ? "Add your first destination" : "Add another destination"}
          </Box>
        )}

        {!isLive && fileStreamLive && platforms.length > 0 && (
          <Typography sx={{ color: COLORS.muted, fontSize: 12.5, lineHeight: 1.55, pt: 0.5 }}>
            A scheduled MP4 is streaming. Start and stop it from the MP4 tab in Source.
          </Typography>
        )}
      </Stack>

      <ConfirmDialog
        open={stopOpen}
        onClose={() => setStopOpen(false)}
        onConfirm={() => stopPlatformApi(stopTarget.id)}
        icon={<StopRoundedIcon />}
        title={stopTarget ? `Stop sending to ${stopTarget.name}?` : ""}
        body={stopTarget
          ? `Viewers on ${stopTarget.name}${stopTarget.label && stopTarget.label !== stopTarget.name ? ` (${stopTarget.label})` : ""} will see the broadcast end. Your other destinations keep running, and you can restart this one while you're live.`
          : ""}
        cancelLabel="Keep streaming"
        confirmLabel={stopTarget ? `Stop ${stopTarget.name}` : "Stop"}
      />
    </Box>
  );
}
